import React, { useState } from 'react'
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined'
import LightModeOutlined from '@mui/icons-material/LightModeOutlined'
import SettingsBrightness from '@mui/icons-material/SettingsBrightness'
import IconButton from '@mui/material/IconButton'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import { ColorModePreference, useColorMode } from 'contexts/ColorModeContext'

const OPTIONS: { value: ColorModePreference; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
]

type ThemeModeToggleProps = {
  /** `nav` fits the sidebar list; `icon` is the compact toolbar control. */
  variant?: 'icon' | 'nav'
  comfortable?: boolean
  itemSx?: object
  iconSx?: object
}

export const ThemeModeToggle: React.FC<ThemeModeToggleProps> = ({
  variant = 'icon',
  comfortable = true,
  itemSx,
  iconSx,
}) => {
  const { preference, resolvedMode, setPreference } = useColorMode()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const ModeIcon = resolvedMode === 'dark' ? DarkModeOutlined : LightModeOutlined
  const preferenceLabel =
    preference === 'system' ? 'System' : preference === 'dark' ? 'Dark' : 'Light'
  const tooltipLabel = `Theme · ${preferenceLabel}`

  const menu = (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      MenuListProps={{ 'aria-label': 'Color theme' }}
      // Open into the main canvas (right of the rail) so the menu never pushes
      // the page wider than the viewport.
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            maxWidth: 'min(220px, calc(100vw - 24px))',
            overflowX: 'hidden',
          },
        },
      }}
    >
      {OPTIONS.map(option => (
        <MenuItem
          key={option.value}
          selected={preference === option.value}
          onClick={() => {
            setPreference(option.value)
            setAnchorEl(null)
          }}
        >
          {option.value === 'light' ? (
            <LightModeOutlined fontSize='small' sx={{ mr: 1 }} />
          ) : option.value === 'dark' ? (
            <DarkModeOutlined fontSize='small' sx={{ mr: 1 }} />
          ) : (
            <SettingsBrightness fontSize='small' sx={{ mr: 1 }} />
          )}
          {option.label}
        </MenuItem>
      ))}
    </Menu>
  )

  if (variant === 'nav') {
    return (
      <>
        <Tooltip title={comfortable ? '' : tooltipLabel} placement='right'>
          <ListItemButton
            onClick={event => setAnchorEl(event.currentTarget)}
            aria-label='Open theme menu'
            aria-haspopup='menu'
            aria-expanded={open ? 'true' : undefined}
            sx={{
              ...itemSx,
              maxWidth: '100%',
            }}
          >
            <ListItemIcon sx={iconSx}>
              <ModeIcon />
            </ListItemIcon>
            {comfortable ? (
              <ListItemText
                primary='Theme'
                secondary={preferenceLabel}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{
                  noWrap: true,
                  sx: { color: 'inherit', opacity: 0.72, fontSize: '0.7rem' },
                }}
                sx={{ minWidth: 0, my: 0 }}
              />
            ) : null}
          </ListItemButton>
        </Tooltip>
        {menu}
      </>
    )
  }

  return (
    <>
      <Tooltip title={tooltipLabel}>
        <IconButton
          color='inherit'
          aria-label='Open theme menu'
          aria-haspopup='menu'
          aria-expanded={open ? 'true' : undefined}
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          <ModeIcon />
        </IconButton>
      </Tooltip>
      {menu}
    </>
  )
}
